import type {GeoRefAddress} from "@reva/shared/geo-reference";
import type {AvmSpecSegment} from "@reva/shared/reva-spec";
import {SEGMENT} from "@reva/shared/reva-spec";
import type {TimelineObjectType, TimelineRegionalLevel} from "@reva/shared/timelines";

export interface ObjectTypeDef {
    value: TimelineObjectType;
    labelI18n: string;
}

/** The object types shown across the market tabs, covering dropdown options and label lookup. Segment mapping (sale/rent) comes from SEGMENT_BY_OBJECT_TYPE. */
export const OBJECT_TYPES: ObjectTypeDef[] = [
    {value: SEGMENT.WHG_K, labelI18n: "object_types.flat"},
    {value: SEGMENT.EZFH_K, labelI18n: "object_types.EFH_ZFH"},
    {value: SEGMENT.MFHWGH_K, labelI18n: "object_types.MFH_WGH_long"},
    {value: SEGMENT.BUERO_K, labelI18n: "object_types.office_practice"},
];

export function getObjectTypeLabelI18n(value: string): string {
    return OBJECT_TYPES.find((o) => o.value === value)?.labelI18n ?? "";
}

export const REGIONAL_LEVEL_DATA: Record<
    TimelineRegionalLevel,
    {
        code: number;
        nameI18n: string;
        /** Which field of the georeferenced address names this level; absent for NEIGHBORHOOD. */
        address_field?: keyof GeoRefAddress;
    }
> = {
    NEIGHBORHOOD: {
        code: 1,
        nameI18n: "regional_levels.neighborhood",
    },
    POSTCODE: {
        code: 2,
        nameI18n: "regional_levels.postcode",
        address_field: "postcode",
    },
    MUNICIPALITY: {
        code: 3,
        nameI18n: "regional_levels.municipality",
        address_field: "municipality",
    },
    DISTRICT: {
        code: 4,
        nameI18n: "regional_levels.district",
        address_field: "district",
    },
    REGION: {
        code: 5,
        nameI18n: "regional_levels.region",
        address_field: "region",
    },
};

/**
 * Get (initial) Timeline Object type for a configured segment in the current valuation. For
 * GRST_K (unbebautes Grundstück), the price development follows the planned building project
 * (parameter "building_project", categories 1=EZFH, 2=MFH) instead of the segment itself (#19207).
 */
export const getObjectTypeBySegment = (
    segment: AvmSpecSegment,
    buildingProject?: number | string | null,
): TimelineObjectType => {
    if (segment === SEGMENT.GRST_K) return Number(buildingProject) === 2 ? SEGMENT.MFHWGH_K : SEGMENT.EZFH_K;
    return segment === SEGMENT.EZFH_K || segment === SEGMENT.MFHWGH_K ? segment : SEGMENT.WHG_K;
};
